/**
 * Central launch + company configuration.
 *
 * PUBLIC LAUNCH CHECKLIST:
 *  1. Set DEFAULT_LAUNCH_STATE to 'public-launch'.
 *  2. Fill in APP_STORE_URL and GOOGLE_PLAY_URL.
 *  3. Add the OFFICIAL Apple and Google badge artwork to STORE_BADGE_ASSETS.
 *     Until those assets are set, the site renders plain text download links
 *     instead of home-made badge lookalikes (see components/StoreBadges.tsx).
 *
 * See docs/internal-pre-launch-checklist.md for the outstanding legal and
 * feature-verification items. That file is internal and is not rendered.
 */

export type LaunchState = 'pre-launch' | 'public-launch'

export const DEFAULT_LAUNCH_STATE: LaunchState = 'pre-launch'

/** Real store URLs go here at public launch. Left empty deliberately — do not guess. */
export const APP_STORE_URL = ''
export const GOOGLE_PLAY_URL = ''

/**
 * Official store badge artwork, downloaded from Apple's Marketing Resources and
 * Google Play's Brand Guidelines. Home-made lookalikes breach both sets of
 * brand guidelines, so these must be the official assets — leave empty until
 * they are in place.
 */
export const STORE_BADGE_ASSETS = {
  appStoreBadgeSrc: '',
  googlePlayBadgeSrc: '',
}

export const SITE_URL = 'https://www.kittypot.com'

export const SUPPORT_EMAIL = 'support@kittypot.com'

export const COMPANY = {
  legalName: 'KittyPot Ltd',
  brandName: 'KittyPot',
  registrationNumber: '13695043',
  /** Registered office. Confirmed against Companies House. */
  addressLines: [
    'KittyPot Ltd',
    'Flat 2 Minavon',
    'Tyn Y Parc Road',
    'Cardiff',
    'South Glamorgan',
    'Wales',
    'CF14 6BE',
  ],
  copyrightYear: 2026,
}

/**
 * Master switch for non-essential technologies (analytics / marketing).
 *
 * CURRENT STATE: false. The website loads NO analytics or marketing scripts at
 * all, sets only strictly necessary storage, and the consent banner is hidden.
 *
 * Setting this to true does two things and only two things:
 *  1. Shows the consent banner (components/CookieBanner.tsx).
 *  2. Switches the Cookie Policy's analytics section to the "with consent"
 *     wording.
 *
 * It does NOT itself load any script, because there are no scripts to load yet.
 * When you add analytics, add the loader inside loadConsentedScripts() in
 * utils/consent.ts — that function is the single gate and is only ever called
 * after consent is granted.
 */
export const USES_NON_ESSENTIAL_COOKIES = false
